// Transaction type values from the backend

export enum CashFlowColumnAlias {
  DATE_OF_TRANSACTION = 'date_of_transaction',
  AMOUNT = 'amount',
  TRANSACTION_TYPE = 'transaction_type',
  CASH_FLOW_TYPE = 'cash_flow_type',
  OWNER = 'owner',
  ENTITY = 'entity',
  ALL_SECURITY = 'all_security',
}

export enum CashFlowColumnValue {
  DATE_OF_TRANSACTION = 'Date of Transaction',
  AMOUNT = 'Amount',
  // Label-only rename: the payload alias stays `transaction_type`.
  TRANSACTION_TYPE = 'Description',
  CASH_FLOW_TYPE = 'Type',
  OWNER = 'Owner',
  ENTITY = 'Entity',
  ALL_SECURITY = 'Security',
}

export const CashFlowColumnNames: CashFlowColumnAlias[] = [
  CashFlowColumnAlias.DATE_OF_TRANSACTION,
  CashFlowColumnAlias.AMOUNT,
  CashFlowColumnAlias.TRANSACTION_TYPE,
  CashFlowColumnAlias.CASH_FLOW_TYPE,
  CashFlowColumnAlias.OWNER,
  CashFlowColumnAlias.ENTITY,
  CashFlowColumnAlias.ALL_SECURITY,
];

// Explicit designation of a ledger row (mirrors backend CashFlowType choices).
// NULL = untyped legacy row: aggregations fall back to the sign heuristic.
export const CASH_FLOW_TYPE = {
  INVESTED_CAPITAL: 'invested_capital',
  PROCEEDS: 'proceeds',
} as const;

export type CashFlowType = typeof CASH_FLOW_TYPE[keyof typeof CASH_FLOW_TYPE];

export const CASH_FLOW_TYPE_OPTIONS = [
  { value: CASH_FLOW_TYPE.INVESTED_CAPITAL, label: 'Investment' },
  { value: CASH_FLOW_TYPE.PROCEEDS, label: 'Proceeds' },
];

export const CASH_FLOW_TYPE_TOOLTIP =
  'Investment amounts are shown with the opposite sign of the stored cash flow; Proceeds are shown as stored.';

/**
 * Classification rules shared by the summary hooks (mirrors the backend
 * aggregation predicates): typed rows bucket by designation regardless of
 * sign; untyped rows keep the legacy sign heuristic (negative = invested
 * capital, positive = proceeds). A value that is neither null nor a known
 * designation matches neither predicate, exactly as the backend drops it.
 */
export const isInvestedCapitalFlow = (
  cashFlowType: string | null | undefined,
  signedAmount: number
): boolean =>
  cashFlowType == null ? signedAmount < 0 : cashFlowType === CASH_FLOW_TYPE.INVESTED_CAPITAL;

export const isProceedsFlow = (
  cashFlowType: string | null | undefined,
  signedAmount: number
): boolean =>
  cashFlowType === CASH_FLOW_TYPE.PROCEEDS
  || (cashFlowType == null && signedAmount > 0);

export const TRANSACTION_TYPE = {
  INVESTMENT: 'INVESTMENT',
  REALIZED_PROCEEDS: 'REALIZED_PROCEEDS',
  UNREALIZED_VALUE: 'UNREALIZED_VALUE',
} as const;

export type TransactionType = typeof TRANSACTION_TYPE[keyof typeof TRANSACTION_TYPE];

// Transaction types that are outflows (should have negative amounts)
export const OUTFLOW_TRANSACTION_TYPES: TransactionType[] = [
  TRANSACTION_TYPE.INVESTMENT,
];

// Transaction types that are inflows (should have positive amounts)
export const INFLOW_TRANSACTION_TYPES: TransactionType[] = [
  TRANSACTION_TYPE.REALIZED_PROCEEDS,
  TRANSACTION_TYPE.UNREALIZED_VALUE,
];

// CSS class names for row coloring based on is_virtual
export const ROW_CLASS = {
  // Green = virtual/system-calculated rows (read-only)
  VIRTUAL: 'cash-flow-row--green',
  // Blue = manually added rows (editable)
  MANUAL: 'cash-flow-row--blue',
} as const;

/**
 * Get the CSS class name for a row based on whether it's virtual (system-calculated) or manual
 * @param isVirtual - true if the row is system-calculated, false if manually added
 */
export const getRowClassByVirtual = (isVirtual: boolean): string =>
  isVirtual ? ROW_CLASS.VIRTUAL : ROW_CLASS.MANUAL;
